import React, { useState, useEffect } from 'react';
import { CaretDown, CaretUp, Buildings, Sliders, Info, CheckCircle, WarningCircle, Circle } from '@phosphor-icons/react';
import type { ParseDatasetResult, ProvenanceEntry } from '../../services/projects';

/*  ──────────────────────────────────────────────────────────────
    Project intake — AirCap terminal config + advanced DACAMS params.

    Contract (what the backend actually reads from sme_parameters):
      · aircap_config  -> aircap_logic.capacity.compute_ptbc(cfg, sme)
      · weight_tcf / weight_wcf / weight_wif / weight_nof / weight_ccf
      · ips_weight_stress / ips_weight_complexity / ips_weight_saturation
      · v_ref_mps

    Fallback rule (mirrors the backend, Pablo 2026-07-15/16):
      A field left EMPTY is not sent, so the backend applies its own fallback:
        - LoS / service times -> IATA ADRM "Optimum" defaults (per-key merge)
        - CF weights          -> AODB auto-derived value, else canonical default
      Only explicitly entered values are sent as overrides. This keeps the
      glass-box honest: we never fabricate a value the user did not provide.

    Pre-fill (UX-1, Pablo 2026-08-04): when a dataset is uploaded in the wizard,
    POST /projects/parse-dataset returns an auto-built aircap_config + provenance.
    applyParseResult() merges it into this value (EDITABLE); `provenance` drives
    the per-field badges (green=modelled / amber=missing→enter / grey=default).
    Because an explicit aircap_config short-circuits the backend auto-build
    (analysis.py:635), the sent config must be a COMPLETE superset — inventory +
    service_sec + shares + pax_per_movement — or compute_ptbc's pax/h->mov/h
    conversion (resolve_pax_per_movement) degrades to the default ppm.
    ────────────────────────────────────────────────────────────── */

export type LosSource = 'adrm' | 'custom';

export interface AdvancedConfigValue {
  losSource: LosSource;
  inventory: Record<string, number | ''>;
  serviceSec: Record<string, number | ''>;
  wmaxMin: Record<string, number | ''>;
  securityLanePaxh: number | '';
  beltPaxh: number | '';
  shares: Record<string, number | ''>;
  ops: Record<string, number | ''>;
  cfWeights: Record<string, number | ''>;
  ipsWeights: { stress: number | ''; complexity: number | ''; saturation: number | '' };
  vRefKt: number | '';           // reference taxi speed in KNOTS (converted to m/s on send)
  paxPerMovement: number | '';   // pax/movement (dataset-derived; feeds pax/h->mov/h)
  bankThresholds: { arr: number | ''; dep: number | ''; total: number | '' };
}

/** IATA ADRM "Optimum" reference values — shown as placeholders, never auto-sent. */
export const ADRM_SERVICE_SEC: Record<string, number> = {
  checkin: 120, kiosk: 90, bagdrop: 60, emigration: 30, immigration: 45,
};
export const ADRM_WMAX_MIN: Record<string, number> = {
  checkin: 15, kiosk: 5, bagdrop: 5, security: 7, emigration: 10, immigration: 15,
};
export const ADRM_SECURITY_LANE_PAXH = 225;
export const ADRM_BELT_PAXH = 300;
/** Canonical CF weight defaults (backend `_cf_default_weights`, renormalised to 1). */
export const CF_DEFAULTS: Record<string, number> = {
  tcf: 0.25, wcf: 0.30, wif: 0.20, nof: 0.15, ccf: 0.10,
};
/** Knots -> m/s (v_ref is entered in kt for operators, sent as v_ref_mps). */
export const MS_PER_KT = 0.514444;
/** Backend default v_ref = 7.7 m/s ≈ 15 kt (shown as the kt placeholder). */
export const VREF_DEFAULT_KT = Math.round(7.7 / MS_PER_KT);

export const emptyAdvancedConfig = (): AdvancedConfigValue => ({
  losSource: 'adrm',
  inventory: { checkin: '', kiosk: '', bagdrop: '', security: '', emigration: '', immigration: '', baggage: '' },
  serviceSec: { checkin: '', kiosk: '', bagdrop: '', emigration: '', immigration: '' },
  wmaxMin: { checkin: '', kiosk: '', bagdrop: '', security: '', emigration: '', immigration: '' },
  securityLanePaxh: '',
  beltPaxh: '',
  shares: { checkin_traditional_pct: '', selfservice_pct: '', bagdrop_pct: '', pax_with_baggage_pct: '' },
  ops: { arr_ratio: '', operational_availability: '', walking_factor: '', works_factor: '', curfew_factor: '' },
  cfWeights: { tcf: '', wcf: '', wif: '', nof: '', ccf: '' },
  ipsWeights: { stress: '', complexity: '', saturation: '' },
  vRefKt: '',
  paxPerMovement: '',
  bankThresholds: { arr: '', dep: '', total: '' },
});

/**
 * Merge a POST /projects/parse-dataset result into the advanced config, EDITABLE.
 * Carries the full dataset-derived set (inventory + service_sec + shares +
 * pax_per_movement + security_lane_paxh). Sets losSource='custom' when the
 * dataset supplies real service rates, so buildSmeConfig actually sends them
 * (an explicit aircap_config replaces, not merges, the backend auto-build).
 */
export function applyParseResult(cfg: AdvancedConfigValue, res: ParseDatasetResult): AdvancedConfigValue {
  const ac = res.aircap_config || {};
  const next: AdvancedConfigValue = {
    ...cfg,
    inventory: { ...cfg.inventory },
    serviceSec: { ...cfg.serviceSec },
    shares: { ...cfg.shares },
  };
  if (ac.inventory) {
    for (const [k, v] of Object.entries(ac.inventory)) {
      if (typeof v === 'number' && !Number.isNaN(v)) next.inventory[k] = v;
    }
  }
  if (ac.shares) {
    for (const [k, v] of Object.entries(ac.shares)) {
      if (typeof v === 'number' && !Number.isNaN(v)) next.shares[k] = v;
    }
  }
  const hasRates = (ac.service_sec && Object.keys(ac.service_sec).length > 0) || ac.security_lane_paxh != null;
  if (hasRates) next.losSource = 'custom';
  if (ac.service_sec) {
    for (const [k, v] of Object.entries(ac.service_sec)) {
      if (typeof v === 'number' && !Number.isNaN(v)) next.serviceSec[k] = v;
    }
  }
  if (ac.security_lane_paxh != null && !Number.isNaN(ac.security_lane_paxh)) {
    next.securityLanePaxh = ac.security_lane_paxh;
  }
  const ppm = ac.pax_per_movement ?? res.pax_per_movement;
  if (ppm != null && !Number.isNaN(ppm)) next.paxPerMovement = ppm;
  return next;
}

/**
 * Inverse of buildSmeConfig: hydrate an AdvancedConfigValue from a project's
 * stored sme_parameters, so the same card can review/edit the terminal config
 * AFTER creation (project config modal). Missing keys stay '' (backend default).
 */
export function smeConfigToAdvanced(sme: Record<string, any> | undefined | null): AdvancedConfigValue {
  const base = emptyAdvancedConfig();
  if (!sme) return base;
  const ac = (sme.aircap_config || {}) as Record<string, any>;
  const pick = (obj: Record<string, any>, k: string): number | '' =>
    (obj && obj[k] != null && !Number.isNaN(obj[k])) ? Number(obj[k]) : '';
  return {
    ...base,
    losSource: ac.los_source === 'custom' ? 'custom' : 'adrm',
    inventory: { ...base.inventory, ...(ac.inventory || {}) },
    serviceSec: { ...base.serviceSec, ...(ac.service_sec || {}) },
    wmaxMin: { ...base.wmaxMin, ...(ac.wmax_min || {}) },
    securityLanePaxh: pick(ac, 'security_lane_paxh'),
    beltPaxh: pick(ac, 'belt_paxh'),
    shares: { ...base.shares, ...(ac.shares || {}) },
    ops: {
      arr_ratio: pick(ac, 'arr_ratio'),
      operational_availability: pick(ac, 'operational_availability'),
      walking_factor: pick(ac, 'walking_factor'),
      works_factor: pick(ac, 'works_factor'),
      curfew_factor: pick(ac, 'curfew_factor'),
    },
    cfWeights: {
      tcf: pick(sme, 'weight_tcf'), wcf: pick(sme, 'weight_wcf'), wif: pick(sme, 'weight_wif'),
      nof: pick(sme, 'weight_nof'), ccf: pick(sme, 'weight_ccf'),
    },
    ipsWeights: {
      stress: pick(sme, 'ips_weight_stress'),
      complexity: pick(sme, 'ips_weight_complexity'),
      saturation: pick(sme, 'ips_weight_saturation'),
    },
    vRefKt: sme.v_ref_mps != null && !Number.isNaN(sme.v_ref_mps)
      ? Number((Number(sme.v_ref_mps) / MS_PER_KT).toFixed(2)) : '',
    paxPerMovement: pick(ac, 'pax_per_movement'),
    bankThresholds: {
      arr: pick(sme.bank_thresholds || {}, 'arr'),
      dep: pick(sme.bank_thresholds || {}, 'dep'),
      total: pick(sme.bank_thresholds || {}, 'total'),
    },
  };
}

const num = (v: number | '' | undefined): number | undefined =>
  v === '' || v === undefined || Number.isNaN(v as number) ? undefined : Number(v);

/** Keep only explicitly-filled entries (empty => backend fallback applies). */
const filled = (obj: Record<string, number | ''>): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(obj)) {
    const n = num(v);
    if (n !== undefined) out[k] = n;
  }
  return out;
};

/**
 * Build the sme_parameters fragment for the project payload.
 * Omits everything the user left empty so the backend's documented fallbacks
 * (ADRM defaults / AODB auto-derived weights) apply and are flagged as such.
 */
export function buildSmeConfig(cfg: AdvancedConfigValue): Record<string, unknown> {
  const inventory = filled(cfg.inventory);
  const shares = filled(cfg.shares);
  const ops = filled(cfg.ops);

  const aircap: Record<string, unknown> = {};
  if (Object.keys(inventory).length) aircap.inventory = inventory;
  if (Object.keys(shares).length) aircap.shares = shares;
  // pax/movement (dataset-derived) — carried so compute_ptbc's pax/h->mov/h
  // conversion has a real ppm even when the annual pax/mov totals are left blank.
  const ppm = num(cfg.paxPerMovement);
  if (ppm !== undefined) aircap.pax_per_movement = ppm;
  Object.assign(aircap, ops);
  // LoS overrides only in "custom" mode; per-key, so blanks still fall back to ADRM.
  if (cfg.losSource === 'custom') {
    const svc = filled(cfg.serviceSec);
    const wmax = filled(cfg.wmaxMin);
    if (Object.keys(svc).length) aircap.service_sec = svc;
    if (Object.keys(wmax).length) aircap.wmax_min = wmax;
    const lane = num(cfg.securityLanePaxh);
    const belt = num(cfg.beltPaxh);
    if (lane !== undefined) aircap.security_lane_paxh = lane;
    if (belt !== undefined) aircap.belt_paxh = belt;
  }
  aircap.los_source = cfg.losSource; // provenance marker (glass box)

  const out: Record<string, unknown> = {};
  // Gate on INVENTORY: AirCap can only produce a meaningful PTBC from installed
  // positions. Sending an aircap_config without inventory would make the bridge
  // take the AirCap path with nothing to model -> PTBC 0 -> tri-node CC 0. With
  // no inventory we omit it entirely so the backend keeps its scalar/default
  // PTBC path (and flags it as such in the provenance trail).
  if (Object.keys(inventory).length) out.aircap_config = aircap;

  for (const [k, v] of Object.entries(filled(cfg.cfWeights))) out[`weight_${k}`] = v;

  const ips = filled({
    ips_weight_stress: cfg.ipsWeights.stress,
    ips_weight_complexity: cfg.ipsWeights.complexity,
    ips_weight_saturation: cfg.ipsWeights.saturation,
  });
  Object.assign(out, ips);

  // v_ref entered in knots (operator-friendly), sent to the backend in m/s.
  const vrefKt = num(cfg.vRefKt);
  if (vrefKt !== undefined) out.v_ref_mps = Number((vrefKt * MS_PER_KT).toFixed(4));

  // Traffic-bank detection thresholds (movements per 60-min window). Per-key, so
  // a blank keeps the DACAMS default (arr 20 / dep 20 / total 30).
  const bankThr = filled({
    arr: cfg.bankThresholds.arr,
    dep: cfg.bankThresholds.dep,
    total: cfg.bankThresholds.total,
  });
  if (Object.keys(bankThr).length) out.bank_thresholds = bankThr;

  return out;
}

// ── UI primitives (design-system tokens only) ──────────────────

const Field: React.FC<{
  label: string; value: number | ''; onChange: (v: number | '') => void;
  placeholder?: string; step?: string; unit?: string; disabled?: boolean;
  badge?: React.ReactNode;
}> = ({ label, value, onChange, placeholder, step = 'any', unit, disabled, badge }) => (
  <div>
    <div className="flex items-center justify-between mb-1 gap-1">
      <label className="block text-[10px] font-semibold uppercase tracking-wider"
             style={{ color: 'var(--text-tertiary)' }}>{label}</label>
      {badge}
    </div>
    <div className="flex items-center rounded-lg border transition-colors"
         style={{ background: disabled ? 'var(--surface-inset)' : 'var(--surface-raised)', borderColor: 'var(--border)' }}>
      <input
        type="number" step={step} disabled={disabled}
        className="w-full bg-transparent px-2.5 py-2 text-sm outline-none font-mono disabled:opacity-60"
        style={{ color: 'var(--text-primary)' }}
        value={value} placeholder={placeholder}
        onWheel={(e) => e.currentTarget.blur()}
        onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
      />
      {unit && <span className="pr-2 text-[10px] select-none" style={{ color: 'var(--text-tertiary)' }}>{unit}</span>}
    </div>
  </div>
);

// ── Provenance badge (glass box): where a pre-filled value comes from ──────────
type BadgeKind = 'dataset' | 'introduce' | 'manual' | 'default';

/** Map a provenance entry + whether the field currently holds a value to a badge. */
function badgeKind(entry: ProvenanceEntry | undefined, hasValue: boolean): BadgeKind {
  if (!entry || entry.status === 'defaulted' || entry.status === 'default') {
    return hasValue ? 'manual' : 'default';
  }
  if (entry.status === 'modelled') return 'dataset';
  // status === 'missing'
  return hasValue ? 'manual' : 'introduce';
}

const ProvBadge: React.FC<{ entry?: ProvenanceEntry; hasValue: boolean; es: boolean; defaultLabel?: string }> = ({ entry, hasValue, es, defaultLabel }) => {
  const kind = badgeKind(entry, hasValue);
  const map: Record<BadgeKind, { color: string; label: string; Icon: React.ElementType }> = {
    dataset: { color: 'var(--good)', label: es ? 'del dataset' : 'from dataset', Icon: CheckCircle },
    manual: { color: 'var(--good)', label: es ? 'introducido' : 'entered', Icon: CheckCircle },
    introduce: { color: 'var(--aipron-warning)', label: es ? 'falta → introduce' : 'missing → enter', Icon: WarningCircle },
    // defaultLabel lets LoS fields say "IATA ADRM" (the actual fallback source)
    // instead of the generic "default" used for non-ADRM fields (e.g. inventory).
    default: { color: 'var(--text-tertiary)', label: defaultLabel ?? 'default', Icon: Circle },
  };
  const { color, label, Icon } = map[kind];
  return (
    <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide whitespace-nowrap"
          style={{ color }} title={entry?.detail || undefined}>
      <Icon size={11} weight="fill" />
      {label}
    </span>
  );
};

const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <div className="mb-6">
    <p className="text-[11px] font-semibold uppercase tracking-[0.1em] mb-1" style={{ color: 'var(--text-secondary)' }}>{title}</p>
    {hint && <p className="text-[11px] mb-3" style={{ color: 'var(--text-tertiary)' }}>{hint}</p>}
    {children}
  </div>
);

interface Props {
  value: AdvancedConfigValue;
  onChange: (v: AdvancedConfigValue) => void;
  language: string;
  /** Provenance from POST /projects/parse-dataset — drives per-field badges.
   *  Undefined (no dataset uploaded yet) => no badges shown. */
  provenance?: ParseDatasetResult['provenance'];
  /** Show the "DACAMS parameters" sub-section (CF/IPS weights, v_ref, bank
   *  thresholds). Default true (wizard). Set false when embedding in a context
   *  that owns those params elsewhere (e.g. the project config modal, which has
   *  its own CF-weights editor) — the card then shows only the terminal config. */
  showAdvancedParams?: boolean;
  /** Start expanded (e.g. when embedded in a modal). Default collapsed. */
  defaultOpen?: boolean;
}

const ProjectAdvancedConfig: React.FC<Props> = ({ value, onChange, language, provenance, showAdvancedParams = true, defaultOpen = false }) => {
  const es = language === 'es';
  const [open, setOpen] = useState(defaultOpen);
  // Auto-expand the card the moment a dataset is parsed, so the pre-filled
  // review (badges + editable values) is visible without an extra click.
  useEffect(() => { if (provenance) setOpen(true); }, [provenance]);
  const set = (patch: Partial<AdvancedConfigValue>) => onChange({ ...value, ...patch });
  const setIn = (key: 'inventory' | 'serviceSec' | 'wmaxMin' | 'shares' | 'ops' | 'cfWeights', k: string, v: number | '') =>
    onChange({ ...value, [key]: { ...value[key], [k]: v } });

  const custom = value.losSource === 'custom';
  const hasVal = (v: number | '' | undefined) => v !== '' && v !== undefined && !Number.isNaN(v as number);
  // Badge helpers keyed to the parse-dataset provenance shape. No dataset -> null.
  const invBadge = (k: string) => provenance
    ? <ProvBadge entry={provenance.subsystems?.[k]} hasValue={hasVal(value.inventory[k])} es={es} /> : null;
  // LoS fields (service times, max waits, security, belt): the fallback is the
  // IATA ADRM generic value, so the "default" badge reads "IATA ADRM" explicitly.
  const rateBadge = (k: string, v: number | '' | undefined) => provenance
    ? <ProvBadge entry={provenance.rates?.[k]} hasValue={hasVal(v)} es={es} defaultLabel="IATA ADRM" /> : null;
  const losBadge = (v: number | '' | undefined) => provenance
    ? <ProvBadge entry={undefined} hasValue={hasVal(v)} es={es} defaultLabel="IATA ADRM" /> : null;
  const shareBadge = (v: number | '' | undefined) => provenance
    ? <ProvBadge entry={provenance.shares} hasValue={hasVal(v)} es={es} /> : null;
  const ppmBadge = provenance
    ? <ProvBadge entry={provenance.pax_per_movement} hasValue={hasVal(value.paxPerMovement)} es={es} /> : null;

  return (
    <div className="rounded-xl border col-span-full"
         style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center justify-between p-6">
        <span className="flex items-center gap-2 text-lg font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>
          <Buildings size={20} style={{ color: 'var(--aipron-accent)' }} />
          {es ? 'AirCap (terminal) y parámetros avanzados' : 'AirCap (terminal) and advanced parameters'}
        </span>
        <span className="flex items-center gap-3">
          <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            {es ? 'Opcional — vacío usa los valores por defecto documentados' : 'Optional — blank uses the documented defaults'}
          </span>
          {open ? <CaretUp size={16} /> : <CaretDown size={16} />}
        </span>
      </button>

      {open && (
        <div className="px-6 pb-6">
          <div className="flex items-start gap-2 rounded-lg border p-3 mb-6"
               style={{ background: 'var(--accent-soft)', borderColor: 'var(--border)' }}>
            <Info size={15} style={{ color: 'var(--aipron-accent)' }} className="mt-0.5 flex-none" />
            <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              {es
                ? 'Todo campo vacío se omite: el backend aplica su valor por defecto documentado (IATA ADRM para LoS; auto-derivado del AODB para los pesos CF) y lo marca como "default" en la trazabilidad.'
                : 'Any blank field is omitted: the backend applies its documented default (IATA ADRM for LoS; AODB auto-derived for CF weights) and flags it as "default" in the provenance trail.'}
            </p>
          </div>

          {/* ── Auto-fill review summary (only when a dataset was parsed) ── */}
          {provenance && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border p-3 mb-6"
                 style={{ background: 'var(--surface-inset)', borderColor: 'var(--border)' }}>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: 'var(--good)' }}>
                <CheckCircle size={14} weight="fill" />
                {(provenance.modelled_subsystems?.length ?? 0)} {es ? 'del dataset' : 'from dataset'}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: 'var(--aipron-warning)' }}>
                <WarningCircle size={14} weight="fill" />
                {(provenance.missing_subsystems?.length ?? 0)} {es ? 'por introducir' : 'to enter'}
              </span>
              {hasVal(value.paxPerMovement) && (
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  {es ? 'pax/mov' : 'pax/mov'}: <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{value.paxPerMovement}</span>
                </span>
              )}
              <span className="text-[11px] ml-auto" style={{ color: 'var(--text-tertiary)' }}>
                {es
                  ? 'Revisa y edita. Verde = del dataset · ámbar = falta, introdúcelo · gris = valor por defecto.'
                  : 'Review and edit. Green = from dataset · amber = missing, enter it · grey = default.'}
              </span>
            </div>
          )}

          {/* ── LoS source toggle ── */}
          <Section title={es ? 'Nivel de servicio (LoS)' : 'Level of Service (LoS)'}
                   hint={es
                     ? 'Fuente de los tiempos de servicio y esperas máximas. "Propios del aeropuerto": introduce los que tengas; los que dejes vacíos caen a ADRM.'
                     : 'Source for service times and maximum queue waits. "Airport\'s own": enter what you have; blanks fall back to ADRM.'}>
            <div className="inline-flex rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
              {(['adrm', 'custom'] as LosSource[]).map((s) => (
                <button key={s} type="button" onClick={() => set({ losSource: s })}
                        className="px-3 py-1.5 text-xs font-semibold transition-colors"
                        style={{
                          background: value.losSource === s ? 'var(--accent-soft)' : 'transparent',
                          color: value.losSource === s ? 'var(--aipron-accent)' : 'var(--text-secondary)',
                        }}>
                  {s === 'adrm' ? (es ? 'ADRM genérico' : 'Generic ADRM') : (es ? 'Propios del aeropuerto' : "Airport's own")}
                </button>
              ))}
            </div>
          </Section>

          {/* ── Terminal inventory ── */}
          <Section title={es ? 'Inventario de terminal' : 'Terminal inventory'}
                   hint={es ? 'Posiciones instaladas. Un subsistema sin datos no se modela (no fuerza PTBC a 0).'
                            : 'Installed positions. A subsystem left blank is not modelled (it will not force PTBC to 0).'}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ['checkin', es ? 'Mostradores' : 'Check-in desks'],
                ['kiosk', es ? 'Kioscos' : 'Kiosks'],
                ['bagdrop', 'Bag drop'],
                ['security', es ? 'Líneas seguridad' : 'Security lanes'],
                ['emigration', es ? 'Pasaportes salida' : 'Emigration'],
                ['immigration', es ? 'Pasaportes llegada' : 'Immigration'],
                ['baggage', es ? 'Cintas equipaje' : 'Baggage belts'],
              ].map(([k, label]) => (
                <Field key={k} label={label} value={value.inventory[k] ?? ''} step="1"
                       onChange={(v) => setIn('inventory', k, v)} placeholder="—"
                       badge={invBadge(k)} />
              ))}
            </div>
          </Section>

          {/* ── pax/movement (dataset-derived; feeds pax/h -> mov/h) ── */}
          {provenance && (
            <Section title={es ? 'Pasajeros por movimiento' : 'Passengers per movement'}
                     hint={es ? 'Derivado del DFFS_Schedule (asientos × factor de ocupación). Convierte la capacidad de la terminal (pax/h) a mov/h.'
                              : 'Derived from DFFS_Schedule (seats × load factor). Converts terminal capacity (pax/h) into mov/h.'}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="pax/mov" unit="pax" value={value.paxPerMovement} step="0.1"
                       onChange={(v) => set({ paxPerMovement: v })} placeholder="—"
                       badge={ppmBadge} />
              </div>
            </Section>
          )}

          {/* ── LoS values (custom only) ── */}
          {custom && (
            <>
              <Section title={es ? 'Tiempo de servicio por pasajero' : 'Service time per passenger'}
                       hint={es ? 'Segundos por pax. Placeholder = valor ADRM que se usaría si lo dejas vacío.'
                                : 'Seconds per pax. Placeholder = the ADRM value used if left blank.'}>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {Object.keys(ADRM_SERVICE_SEC).map((k) => (
                    <Field key={k} label={k} unit="s" value={value.serviceSec[k] ?? ''}
                           onChange={(v) => setIn('serviceSec', k, v)}
                           placeholder={`${ADRM_SERVICE_SEC[k]}`}
                           badge={rateBadge(k, value.serviceSec[k])} />
                  ))}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                  <Field label={es ? 'Seguridad (pax/h/línea)' : 'Security (pax/h/lane)'} unit="pax/h"
                         value={value.securityLanePaxh} onChange={(v) => set({ securityLanePaxh: v })}
                         placeholder={`${ADRM_SECURITY_LANE_PAXH}`}
                         badge={rateBadge('security_lane_paxh', value.securityLanePaxh)} />
                  <Field label={es ? 'Cinta (pax/h)' : 'Belt (pax/h)'} unit="pax/h"
                         value={value.beltPaxh} onChange={(v) => set({ beltPaxh: v })}
                         placeholder={`${ADRM_BELT_PAXH}`} badge={losBadge(value.beltPaxh)} />
                </div>
              </Section>

              <Section title={es ? 'Espera máxima en cola (estándar de servicio)' : 'Maximum queue wait (service standard)'}
                       hint={es ? 'Minutos. Es el tope que acota la capacidad útil vía M/M/s.'
                                : 'Minutes. This is the cap that constrains usable capacity via M/M/s.'}>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  {Object.keys(ADRM_WMAX_MIN).map((k) => (
                    <Field key={k} label={k} unit="min" value={value.wmaxMin[k] ?? ''}
                           onChange={(v) => setIn('wmaxMin', k, v)}
                           placeholder={`${ADRM_WMAX_MIN[k]}`} badge={losBadge(value.wmaxMin[k])} />
                  ))}
                </div>
              </Section>
            </>
          )}

          {/* ── Pax split + operational factors ── */}
          <Section title={es ? 'Reparto de pasajeros y factores operativos' : 'Passenger split and operational factors'}
                   hint={es ? 'Fracciones 0–1. Los factores operativos reducen la capacidad estática a la efectiva (effPTBC).'
                            : 'Fractions 0–1. Operational factors turn static capacity into effective (effPTBC).'}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label={es ? 'Mostrador trad.' : 'Traditional desk'} value={value.shares.checkin_traditional_pct ?? ''} placeholder="0.30" onChange={(v) => setIn('shares', 'checkin_traditional_pct', v)} badge={shareBadge(value.shares.checkin_traditional_pct)} />
              <Field label={es ? 'Autoservicio' : 'Self-service'} value={value.shares.selfservice_pct ?? ''} placeholder="0.50" onChange={(v) => setIn('shares', 'selfservice_pct', v)} badge={shareBadge(value.shares.selfservice_pct)} />
              <Field label="Bag drop" value={value.shares.bagdrop_pct ?? ''} placeholder="0.20" onChange={(v) => setIn('shares', 'bagdrop_pct', v)} />
              <Field label={es ? 'Pax con equipaje' : 'Pax with baggage'} value={value.shares.pax_with_baggage_pct ?? ''} placeholder="0.85" onChange={(v) => setIn('shares', 'pax_with_baggage_pct', v)} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
              <Field label={es ? 'Ratio llegadas' : 'Arrivals ratio'} value={value.ops.arr_ratio ?? ''} placeholder="0.50" onChange={(v) => setIn('ops', 'arr_ratio', v)} />
              <Field label={es ? 'Disponibilidad op.' : 'Operational avail.'} value={value.ops.operational_availability ?? ''} placeholder="1.00" onChange={(v) => setIn('ops', 'operational_availability', v)} />
              <Field label={es ? 'Factor walking' : 'Walking factor'} value={value.ops.walking_factor ?? ''} placeholder="1.00" onChange={(v) => setIn('ops', 'walking_factor', v)} />
              <Field label={es ? 'Factor obras' : 'Works factor'} value={value.ops.works_factor ?? ''} placeholder="1.00" onChange={(v) => setIn('ops', 'works_factor', v)} />
              <Field label={es ? 'Factor curfew' : 'Curfew factor'} value={value.ops.curfew_factor ?? ''} placeholder="1.00" onChange={(v) => setIn('ops', 'curfew_factor', v)} />
            </div>
          </Section>

          {showAdvancedParams && (<>
          <div className="h-px my-6" style={{ background: 'var(--border)' }} />

          {/* ── DACAMS advanced params ── */}
          <p className="flex items-center gap-2 text-sm font-bold mb-4" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>
            <Sliders size={16} style={{ color: 'var(--aipron-accent)' }} />
            {es ? 'Parámetros DACAMS' : 'DACAMS parameters'}
          </p>

          <Section title={es ? 'Pesos del factor de complejidad (CF)' : 'Complexity Factor (CF) weights'}
                   hint={es ? 'Varían por aeropuerto. Vacío = se deriva del AODB; si no hay dato, cae al canon. Se renormalizan a Σ=1.'
                            : 'Airport-specific. Blank = derived from the AODB; failing that, the canonical default. Renormalised to Σ=1.'}>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {(['tcf', 'wcf', 'wif', 'nof', 'ccf'] as const).map((k) => (
                <Field key={k} label={k.toUpperCase()} value={value.cfWeights[k] ?? ''}
                       onChange={(v) => setIn('cfWeights', k, v)} placeholder={`${CF_DEFAULTS[k]}`} />
              ))}
            </div>
          </Section>

          <Section title={es ? 'Pesos del IPS' : 'IPS weights'}
                   hint={es ? 'Se renormalizan a Σ=1. Saturación a 0 reproduce la formulación de 2 términos.'
                            : 'Renormalised to Σ=1. Saturation at 0 reproduces the 2-term formulation.'}>
            <div className="grid grid-cols-3 gap-3">
              <Field label={es ? 'Estrés' : 'Stress'} value={value.ipsWeights.stress} placeholder="0.5"
                     onChange={(v) => set({ ipsWeights: { ...value.ipsWeights, stress: v } })} />
              <Field label={es ? 'Complejidad' : 'Complexity'} value={value.ipsWeights.complexity} placeholder="0.3"
                     onChange={(v) => set({ ipsWeights: { ...value.ipsWeights, complexity: v } })} />
              <Field label={es ? 'Saturación' : 'Saturation'} value={value.ipsWeights.saturation} placeholder="0.2"
                     onChange={(v) => set({ ipsWeights: { ...value.ipsWeights, saturation: v } })} />
            </div>
          </Section>

          <Section title={es ? 'Velocidad de rodaje de referencia' : 'Reference taxi speed'}
                   hint={es ? 'En nudos. Usada para recalcular tiempos de rodaje del grafo (MHT). Se convierte a m/s al enviar.'
                            : 'In knots. Used to recompute graph taxi times (MHT). Converted to m/s on send.'}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="v_ref" unit="kt" value={value.vRefKt} placeholder={`${VREF_DEFAULT_KT}`}
                     onChange={(v) => set({ vRefKt: v })} />
            </div>
          </Section>

          <Section title={es ? 'Umbral de bancos de tráfico' : 'Traffic-bank thresholds'}
                   hint={es ? 'Movimientos por ventana de 60 min para marcar un banco (ola de tráfico). En blanco = default DACAMS (llegadas 20 / salidas 20 / total 30). Ajústalo a la definición de bancos de tu aeropuerto.'
                            : 'Movements per 60-min window to flag a bank (traffic wave). Blank = DACAMS default (arrivals 20 / departures 20 / total 30). Set it to your airport\'s own bank definition.'}>
            <div className="grid grid-cols-3 gap-3">
              <Field label={es ? 'Llegadas' : 'Arrivals'} value={value.bankThresholds.arr} placeholder="20"
                     onChange={(v) => set({ bankThresholds: { ...value.bankThresholds, arr: v } })} />
              <Field label={es ? 'Salidas' : 'Departures'} value={value.bankThresholds.dep} placeholder="20"
                     onChange={(v) => set({ bankThresholds: { ...value.bankThresholds, dep: v } })} />
              <Field label="Total" value={value.bankThresholds.total} placeholder="30"
                     onChange={(v) => set({ bankThresholds: { ...value.bankThresholds, total: v } })} />
            </div>
          </Section>
          </>)}
        </div>
      )}
    </div>
  );
};

export default ProjectAdvancedConfig;
